import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  ClipboardCopy,
  Download,
  MonitorUp,
  RotateCcw,
  Search,
  ShieldCheck,
  Undo2,
  Users,
} from 'lucide-react';
import Eyebrow from '../ui/Eyebrow';
import ManagerAvatar from '../ui/ManagerAvatar';
import keeperMarket2026 from '../../data/keeperMarket2026.json';
import { CastFigure, Reveal } from '../../motion';
import {
  applyKeeperPlacements,
  buildDraftReportCards,
  draftBoardSummary,
  DRAFT_BOARD_STORAGE_KEY,
  flattenMarketPlayers,
  KEEPER_DECISIONS_STORAGE_KEY,
  sanitizeDraftSelections,
} from '../../utils/draftNight';

const ALL_POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'DST', 'K'];

function readStored(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return value && typeof value === 'object' ? value : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key, value) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function keeperCostRound(candidate) {
  const match = String(candidate?.cost || '').match(/^Round (\d+)$/);
  return match ? Number(match[1]) : null;
}

function valueLabel(player, pickOverall) {
  const value = pickOverall - player.overallRank;
  if (value >= 10) return `+${value} value`;
  if (value <= -10) return `${value} reach`;
  return 'near market';
}

export default function DraftNightHQ({ draftOrder, keeperBoard, publishedPicks = [], lockedKeeperState = null }) {
  const order = useMemo(() => draftOrder?.order || [], [draftOrder]);
  const players = useMemo(() => flattenMarketPlayers(keeperMarket2026), []);
  const draftLocked = publishedPicks.length > 0;
  const [selections, setSelections] = useState(() => readStored(DRAFT_BOARD_STORAGE_KEY, {}));
  const [keeperDecisions, setKeeperDecisions] = useState(() => readStored(KEEPER_DECISIONS_STORAGE_KEY, {}));
  const [activeRound, setActiveRound] = useState(1);
  const [activeOverall, setActiveOverall] = useState(null);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState('ALL');
  const [displayMode, setDisplayMode] = useState(false);
  const [copied, setCopied] = useState(false);

  const safeSelections = useMemo(() => sanitizeDraftSelections(
    selections,
    order.map((pick) => pick.overall),
  ), [order, selections]);
  const keeperResult = useMemo(() => applyKeeperPlacements(order, keeperDecisions), [order, keeperDecisions]);
  const keeperSlots = keeperResult.placements;
  const pickedNames = useMemo(() => new Set([
    ...Object.values(safeSelections).map((player) => player.player),
    ...Object.values(keeperDecisions).map((player) => player?.player).filter(Boolean),
  ]), [safeSelections, keeperDecisions]);
  const summary = useMemo(() => draftBoardSummary(order, safeSelections, keeperSlots), [order, safeSelections, keeperSlots]);
  const reportCards = useMemo(() => buildDraftReportCards(order, safeSelections, keeperDecisions), [order, safeSelections, keeperDecisions]);
  const roundPicks = order.filter((pick) => pick.round === activeRound);
  const rounds = [...new Set(order.map((pick) => pick.round))];

  useEffect(() => {
    if (!order.length) return;
    const next = order.find((pick) => !safeSelections[String(pick.overall)] && !keeperSlots[String(pick.overall)]);
    if (activeOverall === null && next) {
      setActiveOverall(next.overall);
      setActiveRound(next.round);
    }
  }, [activeOverall, keeperSlots, order, safeSelections]);

  useEffect(() => writeStored(DRAFT_BOARD_STORAGE_KEY, safeSelections), [safeSelections]);
  useEffect(() => writeStored(KEEPER_DECISIONS_STORAGE_KEY, keeperDecisions), [keeperDecisions]);

  useEffect(() => {
    if (!lockedKeeperState?.published || !lockedKeeperState.isValid || !lockedKeeperState.keepers.length) return;
    const marketByName = new Map(players.map((player) => [player.player, player]));
    const official = Object.fromEntries(lockedKeeperState.keepers.map((keeper) => {
      const market = marketByName.get(keeper.player);
      return [keeper.manager, {
        player: keeper.player,
        position: market?.position || keeper.position,
        nflTeam: market?.nflTeam || keeper.nflTeam,
        cost: keeper.costRound ? `Round ${keeper.costRound}` : 'No pick',
        keeperCostRound: keeper.costRound,
        market: market ? `#${market.overallRank} overall · ${market.position}${market.positionRank}` : 'Outside current top 300',
        marketRank: market?.overallRank || null,
        overallRank: market?.overallRank || null,
        byeWeek: market?.byeWeek || null,
        official: true,
      }];
    }));
    setKeeperDecisions(official);
  }, [lockedKeeperState, players]);

  useEffect(() => {
    if (!publishedPicks.length) return;
    const marketByName = new Map(players.map((player) => [player.player, player]));
    const imported = {};
    publishedPicks.forEach((row) => {
      const overall = Number.parseInt(row.Pick_Overall ?? row.overall ?? row.Overall, 10);
      const name = row.Player_Name ?? row.player ?? row.Player;
      if (!Number.isInteger(overall) || !name) return;
      imported[String(overall)] = marketByName.get(name) || {
        player: name,
        overallRank: 301,
        position: row.Position || 'FLEX',
        positionRank: null,
        nflTeam: row.NFL_Team || 'FA',
        byeWeek: null,
        auctionValue: 0,
      };
    });
    if (Object.keys(imported).length) setSelections(imported);
  }, [players, publishedPicks]);

  const availablePlayers = players.filter((player) => {
    if (pickedNames.has(player.player)) return false;
    if (position !== 'ALL' && player.position !== position) return false;
    const needle = search.trim().toLowerCase();
    return !needle || `${player.player} ${player.position} ${player.nflTeam}`.toLowerCase().includes(needle);
  }).slice(0, 60);

  function selectPick(overall) {
    const pick = order.find((entry) => entry.overall === overall);
    if (!pick || keeperSlots[String(overall)]) return;
    setActiveOverall(overall);
    setActiveRound(pick.round);
  }

  function draftPlayer(player) {
    if (draftLocked || !activeOverall || keeperSlots[String(activeOverall)]) return;
    const nextSelections = { ...safeSelections, [String(activeOverall)]: player };
    setSelections(nextSelections);
    const nextPick = order.find((pick) => (
      pick.overall > activeOverall
        && !nextSelections[String(pick.overall)]
        && !keeperSlots[String(pick.overall)]
    ));
    if (nextPick) {
      setActiveOverall(nextPick.overall);
      setActiveRound(nextPick.round);
    }
  }

  function undoPick(overall) {
    if (draftLocked) return;
    const next = { ...safeSelections };
    delete next[String(overall)];
    setSelections(next);
    selectPick(overall);
  }

  function setKeeper(manager, playerName) {
    if (lockedKeeperState?.published && lockedKeeperState.isValid) return;
    const dossier = keeperBoard.find((entry) => entry.meta.manager === manager);
    const candidate = dossier?.candidates.find((entry) => entry.player === playerName);
    const next = { ...keeperDecisions };
    if (!candidate) {
      delete next[manager];
    } else {
      next[manager] = {
        player: candidate.player,
        position: candidate.position,
        nflTeam: candidate.team,
        cost: candidate.cost,
        keeperCostRound: keeperCostRound(candidate),
        market: candidate.market,
        marketRank: players.find((player) => player.player === candidate.player)?.overallRank || null,
        overallRank: players.find((player) => player.player === candidate.player)?.overallRank || null,
        byeWeek: players.find((player) => player.player === candidate.player)?.byeWeek || null,
      };
      const cleaned = Object.fromEntries(Object.entries(safeSelections).filter(([, player]) => player.player !== candidate.player));
      setSelections(cleaned);
    }
    setKeeperDecisions(next);
  }

  function resetBoard() {
    if (draftLocked) return;
    if (!window.confirm('Clear every locally entered 2026 draft pick? Keeper declarations will stay intact.')) return;
    setSelections({});
    setActiveOverall(null);
    setActiveRound(1);
  }

  function exportBoard() {
    const payload = {
      season: 2026,
      exportedAt: new Date().toISOString(),
      source: keeperMarket2026.source,
      keepers: keeperDecisions,
      picks: order.map((pick) => ({
        ...pick,
        keeper: keeperSlots[String(pick.overall)] || null,
        selection: safeSelections[String(pick.overall)] || null,
      })),
      reportCards,
    };
    const url = URL.createObjectURL(new Blob([`${JSON.stringify(payload, null, 2)}\n`], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bruins09-2026-draft-board.json';
    link.click();
    URL.revokeObjectURL(url);
  }

  async function copyStatus() {
    const text = `Bruins09 2026 Draft: ${summary.completed}/${summary.totalPicks} slots complete · ${summary.remaining} remaining.`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  if (!draftOrder?.isValid || !order.length) return null;

  const activePick = order.find((pick) => pick.overall === activeOverall);
  const featuredManager = activePick?.manager || order[0]?.manager;

  return (
    <section className="border-b border-border bg-bg-deep" data-testid="draft-night-hq">
      <div className="px-6 md:px-14 py-14">
        <Reveal className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_180px_auto] xl:items-end gap-6">
          <div>
            <Eyebrow color="gold">DRAFT NIGHT HQ</Eyebrow>
            <h2 className="mt-3 font-display font-black uppercase leading-[0.9] tracking-normal text-ink-primary"
                style={{ fontSize: 'clamp(38px, 6vw, 82px)' }}>
              Run the room.<br /><span className="text-accent-gold">Keep every receipt.</span>
            </h2>
            <p className="mt-4 max-w-3xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
              A browser-persistent commissioner board powered by Yahoo&apos;s 170-pick order and ESPN&apos;s Aug. 19 top 300. Keeper slots, live picks, roster construction, and market-only report cards stay together.
            </p>
          </div>
          {featuredManager && (
            <div className="paper aspect-[4/5] w-full max-w-[180px] border border-accent-gold/50 justify-self-start xl:justify-self-center">
              <CastFigure name={featuredManager} trigger="hover" sequence={['shock', 'point']} className="absolute inset-0 w-full h-full" />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setDisplayMode((value) => !value)} aria-pressed={displayMode}
              className="inline-flex items-center gap-2 border border-accent-gold px-4 py-3 text-[10px] font-extrabold tracking-[0.18em] text-accent-gold hover:bg-accent-gold hover:text-bg-deep">
              <MonitorUp size={15} /> {displayMode ? 'EXIT DISPLAY' : 'DISPLAY MODE'}
            </button>
            <button type="button" onClick={exportBoard}
              className="inline-flex items-center gap-2 border border-border px-4 py-3 text-[10px] font-extrabold tracking-[0.18em] text-ink-secondary hover:border-accent-gold hover:text-accent-gold">
              <Download size={15} /> EXPORT
            </button>
          </div>
        </Reveal>

        <Reveal delay={80} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <CommandStat label="Board" value={`${summary.completed}/${summary.totalPicks}`} detail={`${summary.percent}% complete`} />
          <CommandStat label="Remaining" value={summary.remaining} detail="Yahoo slots" />
          <CommandStat label="Keepers" value={Object.keys(keeperDecisions).length} detail="locally declared" />
          <CommandStat label="Market" value="Top 300" detail={keeperMarket2026.asOf} />
        </Reveal>

        {!displayMode && (
          <KeeperConsole keeperBoard={keeperBoard} decisions={keeperDecisions} onChange={setKeeper} unresolved={keeperResult.unresolved} locked={Boolean(lockedKeeperState?.published && lockedKeeperState.isValid)} />
        )}

        <div className={`mt-8 grid gap-5 ${displayMode ? 'grid-cols-1' : 'xl:grid-cols-[minmax(0,1.25fr)_minmax(330px,0.75fr)]'}`}>
          <div className="min-w-0">
            <div className="flex flex-wrap gap-2 mb-4">
              {rounds.map((round) => (
                <button key={round} type="button" onClick={() => setActiveRound(round)}
                  className={`px-3 py-2 border text-[10px] font-black tracking-[0.14em] ${round === activeRound ? 'border-accent-gold bg-accent-gold text-bg-deep' : 'border-border text-ink-tertiary hover:border-accent-gold'}`}>
                  R{round}
                </button>
              ))}
            </div>
            <div className="border border-border bg-bg-base overflow-hidden">
              <div className="grid grid-cols-[52px_minmax(0,1fr)_minmax(0,1.35fr)] gap-3 bg-bg-surface px-4 py-3 text-[9px] tracking-[0.18em] text-ink-tertiary uppercase">
                <div>Pick</div><div>Owner</div><div>Selection</div>
              </div>
              {roundPicks.map((pick) => {
                const drafted = safeSelections[String(pick.overall)];
                const keeper = keeperSlots[String(pick.overall)];
                const active = activeOverall === pick.overall;
                return (
                  <div key={pick.overall}
                    className={`relative w-full grid grid-cols-[52px_minmax(0,1fr)_minmax(0,1.35fr)] gap-3 items-center px-4 py-3.5 text-left border-t border-border transition-colors ${active ? 'bg-accent-gold/10 border-l-[3px] border-l-accent-gold' : 'hover:bg-bg-elevated'} ${keeper ? 'cursor-default' : ''}`}>
                    {!keeper && <button type="button" onClick={() => selectPick(pick.overall)} aria-label={`Select pick ${pick.overall} for ${pick.manager}`} className="absolute inset-0 z-0 w-full" />}
                    <div className="relative z-[1] pointer-events-none">
                      <div className="font-display text-xl font-black text-accent-gold">{pick.overall}</div>
                      <div className="text-[9px] text-ink-tertiary">{pick.round}.{String(pick.pick).padStart(2, '0')}</div>
                    </div>
                    <div className="relative z-[1] pointer-events-none min-w-0 flex items-center gap-2">
                      <ManagerAvatar name={pick.manager} size="sm" />
                      <div className="min-w-0"><div className="text-sm font-black truncate">{pick.manager}</div><div className="text-[9px] text-ink-tertiary truncate">{pick.teamName}</div></div>
                    </div>
                    <div className="relative z-[1] pointer-events-none min-w-0">
                      {keeper ? (
                        <><div className="text-sm font-black truncate text-accent-gold">{keeper.player}</div><div className="text-[9px] tracking-[0.12em] text-ink-tertiary">KEEPER · {keeper.position}</div></>
                      ) : drafted ? (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0"><div className="text-sm font-black truncate">{drafted.player}</div><div className="text-[9px] text-ink-tertiary">{drafted.position} · {drafted.nflTeam} · {valueLabel(drafted, pick.overall)}</div></div>
                          {!displayMode && !draftLocked && <button type="button" onClick={() => undoPick(pick.overall)} aria-label={`Undo ${drafted.player}`} className="relative z-10 pointer-events-auto p-2 text-ink-tertiary hover:text-accent-red"><Undo2 size={15} /></button>}
                        </div>
                      ) : (
                        <div className={`text-[10px] tracking-[0.16em] ${active ? 'text-accent-gold' : 'text-ink-muted'}`}>{active ? 'ON THE CLOCK' : 'OPEN'}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {!displayMode && (
            <aside className="bg-bg-surface border-t-[3px] border-accent-gold p-5 md:p-6 min-w-0 self-start xl:sticky xl:top-4">
              <div className="flex items-center justify-between gap-3">
                <div><div className="text-[9px] tracking-[0.2em] text-accent-gold font-extrabold">ON THE CLOCK</div><div className="mt-1 text-xl font-black">{activePick ? `${activePick.manager} · ${activePick.round}.${String(activePick.pick).padStart(2, '0')}` : 'Board complete'}</div></div>
                <Users className="text-accent-gold" />
              </div>
              <label className="mt-5 flex items-center gap-2 bg-bg-deep border border-border px-3 py-2.5">
                <Search size={16} className="text-ink-tertiary" />
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search player, team, position" className="w-full bg-transparent text-sm outline-none placeholder:text-ink-muted" />
              </label>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {ALL_POSITIONS.map((item) => <button key={item} type="button" onClick={() => setPosition(item)} className={`px-2.5 py-1.5 text-[9px] font-black border ${position === item ? 'border-accent-gold bg-accent-gold text-bg-deep' : 'border-border text-ink-tertiary'}`}>{item}</button>)}
              </div>
              <div className="mt-4 max-h-[520px] overflow-y-auto border border-border">
                {availablePlayers.map((player) => (
                  <button key={player.player} type="button" onClick={() => draftPlayer(player)} disabled={!activePick || draftLocked}
                    className="w-full grid grid-cols-[42px_minmax(0,1fr)_54px] gap-2 items-center px-3 py-3 border-b border-border last:border-0 text-left hover:bg-bg-elevated disabled:opacity-40">
                    <div className="font-display text-xl font-black text-accent-gold">{player.overallRank}</div>
                    <div className="min-w-0"><div className="text-sm font-black truncate">{player.player}</div><div className="text-[9px] text-ink-tertiary">{player.nflTeam} · BYE {player.byeWeek || '—'}</div></div>
                    <div className="text-right text-[10px] font-black text-ink-secondary">{player.position}{player.positionRank}</div>
                  </button>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={copyStatus} className="inline-flex items-center gap-2 text-[9px] font-black tracking-[0.14em] text-accent-gold"><ClipboardCopy size={14} />{copied ? 'COPIED' : 'COPY STATUS'}</button>
                {!draftLocked && <button type="button" onClick={resetBoard} className="inline-flex items-center gap-2 text-[9px] font-black tracking-[0.14em] text-accent-red"><RotateCcw size={14} />CLEAR PICKS</button>}
              </div>
              {draftLocked && <p className="mt-4 text-[10px] font-black tracking-[0.14em] text-accent-gold">CANONICAL YAHOO RESULTS LOCKED</p>}
            </aside>
          )}
        </div>

        <KeeperReveal decisions={keeperDecisions} keeperBoard={keeperBoard} />
        <DraftReportCards cards={reportCards} summary={summary} />
      </div>
    </section>
  );
}

function CommandStat({ label, value, detail }) {
  return <div className="bg-bg-surface border-t-[3px] border-accent-gold p-4"><div className="text-[9px] tracking-[0.18em] text-ink-tertiary font-extrabold">{label.toUpperCase()}</div><div className="mt-2 font-display text-3xl md:text-4xl font-black text-accent-gold">{value}</div><div className="mt-1 text-[10px] text-ink-tertiary">{detail}</div></div>;
}

function KeeperConsole({ keeperBoard, decisions, onChange, unresolved, locked }) {
  return (
    <div className="mt-8 bg-bg-base border border-border p-5 md:p-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div><div className="text-[9px] tracking-[0.22em] text-accent-gold font-extrabold">KEEPER PLACEMENT CONSOLE</div><h3 className="mt-1 text-2xl font-black">{locked ? 'Official keepers are locked.' : 'Declare locally. Place automatically.'}</h3></div>
        <div className="text-[10px] text-ink-tertiary italic max-w-md">{locked ? 'The validated canonical keeper file controls this board.' : 'Selections are private to this browser until the commissioner publishes the official keeper file.'}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {keeperBoard.map((dossier) => (
          <label key={dossier.meta.manager} className="bg-bg-surface p-4 border-t-2 border-accent-gold">
            <span className="flex items-center gap-2"><ManagerAvatar name={dossier.meta.manager} size="sm" /><span className="text-sm font-black">{dossier.meta.manager}</span></span>
            <select value={decisions[dossier.meta.manager]?.player || ''} disabled={locked} onChange={(event) => onChange(dossier.meta.manager, event.target.value)} className="mt-3 w-full bg-bg-deep border border-border px-2 py-2 text-[11px] text-ink-secondary disabled:opacity-70">
              <option value="">Awaiting declaration</option>
              {dossier.candidates.map((candidate) => <option key={candidate.player} value={candidate.player}>{candidate.player} · {candidate.cost}</option>)}
            </select>
          </label>
        ))}
      </div>
      {unresolved.length > 0 && <p className="mt-4 text-[11px] text-accent-red">No owned slot exists in the listed cost round for: {unresolved.map((item) => `${item.manager} (${item.cost})`).join(', ')}. Yahoo&apos;s final keeper placement must resolve those traded-pick cases.</p>}
    </div>
  );
}

function KeeperReveal({ decisions, keeperBoard }) {
  const declared = Object.entries(decisions).filter(([, decision]) => decision?.player);
  const draftedValues = declared
    .filter(([, decision]) => decision.keeperCostRound && decision.marketRank)
    .map(([manager, decision]) => ({ manager, ...decision, surplus: decision.keeperCostRound - Math.ceil(decision.marketRank / 10) }))
    .sort((first, second) => second.surplus - first.surplus);
  const noPick = declared.filter(([, decision]) => !decision.keeperCostRound);
  const longestShot = declared
    .filter(([, decision]) => decision.marketRank)
    .map(([manager, decision]) => ({ manager, ...decision }))
    .sort((first, second) => second.marketRank - first.marketRank)[0];
  const positionMix = declared.reduce((counts, [, decision]) => {
    counts[decision.position] = (counts[decision.position] || 0) + 1;
    return counts;
  }, {});

  return (
    <div className="mt-14" data-testid="keeper-reveal-board">
      <Reveal>
        <Eyebrow color="red">KEEPER REVEAL BOARD</Eyebrow>
        <h3 className="mt-2 text-display-xs md:text-display-sm font-black tracking-normal leading-none">The Aug. 26 reveal.</h3>
        <p className="mt-3 max-w-3xl text-sm text-ink-secondary italic">Before the deadline this board shows honest pending states. Locally declared choices appear immediately; official league-wide choices can replace them from the canonical 2026 keeper source after Yahoo locks.</p>
      </Reveal>
      <Reveal delay={80} className="mt-6 grid grid-cols-2 xl:grid-cols-5 gap-3">
        <RevealStat label="Declared" value={`${declared.length}/10`} copy="Local browser board" />
        <RevealStat label="Biggest discount" value={draftedValues[0] ? `+${draftedValues[0].surplus}` : '—'} copy={draftedValues[0] ? `${draftedValues[0].player} · ${draftedValues[0].manager}` : 'Awaiting choices'} />
        <RevealStat label="No-pick keepers" value={noPick.length || '—'} copy={noPick.length ? noPick.map(([, decision]) => decision.player).join(' · ') : 'Awaiting choices'} />
        <RevealStat label="Longest market shot" value={longestShot ? `#${longestShot.marketRank}` : '—'} copy={longestShot ? `${longestShot.player} · ${longestShot.manager}` : 'Awaiting choices'} />
        <RevealStat label="Position mix" value={declared.length || '—'} copy={Object.entries(positionMix).map(([position, count]) => `${position} ${count}`).join(' · ') || 'Awaiting choices'} />
      </Reveal>
      <Reveal delay={140} className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {keeperBoard.map((dossier) => {
          const decision = decisions[dossier.meta.manager];
          const currentLeader = dossier.summaryCards.find((card) => card.label === 'Current leader')?.value;
          const grade = keeperRevealGrade(decision);
          return <article key={dossier.meta.manager} className={`p-5 border-t-[3px] ${decision ? 'bg-bg-surface border-accent-gold' : 'bg-bg-base border-border'}`}>
            <div className="flex items-center gap-3"><ManagerAvatar name={dossier.meta.manager} size="sm" /><div><div className="font-black">{dossier.meta.manager}</div><div className="text-[9px] text-ink-tertiary">{dossier.meta.yahooTeamName}</div></div></div>
            {decision ? <><div className="mt-5 text-lg font-black text-accent-gold">{decision.player}</div><div className="mt-1 text-[10px] tracking-[0.15em] text-ink-tertiary">{decision.position} · {decision.cost.toUpperCase()}</div><div className="mt-3 flex items-center justify-between gap-2"><span className="inline-flex items-center gap-1 text-[9px] text-accent-gold"><Check size={13} /> {decision.official ? 'OFFICIAL KEEPER' : 'LOCAL DECLARATION'}</span><span className="font-display text-2xl font-black text-accent-gold">{grade}</span></div>{currentLeader && currentLeader !== decision.player && <p className="mt-3 border-t border-border pt-3 text-[10px] text-ink-tertiary italic">Passed on current board leader {currentLeader}.</p>}</> : <><div className="mt-5 text-sm font-black text-ink-tertiary">Awaiting declaration</div><div className="mt-2 text-[10px] text-ink-muted">No keeper invented before Yahoo publishes it.</div></>}
          </article>;
        })}
      </Reveal>
    </div>
  );
}

function RevealStat({ label, value, copy }) {
  return <div className="bg-bg-base border-l-[3px] border-accent-gold p-4"><div className="text-[9px] tracking-[0.16em] text-ink-tertiary font-black">{label.toUpperCase()}</div><div className="mt-2 font-display text-3xl font-black text-accent-gold">{value}</div><div className="mt-1 text-[10px] text-ink-tertiary line-clamp-2">{copy}</div></div>;
}

function keeperRevealGrade(decision) {
  if (!decision?.player) return '—';
  if (!decision.keeperCostRound) return 'FREE';
  if (!decision.marketRank) return 'VERIFY';
  const surplus = decision.keeperCostRound - Math.ceil(decision.marketRank / 10);
  if (surplus >= 6) return 'A';
  if (surplus >= 3) return 'B+';
  if (surplus >= 1) return 'B';
  if (surplus === 0) return 'C';
  return 'D';
}

function DraftReportCards({ cards, summary }) {
  return (
    <div className="mt-14" data-testid="draft-report-cards">
      <Reveal>
        <Eyebrow color="gold">POST-DRAFT REPORT CARDS</Eyebrow>
        <h3 className="mt-2 text-display-xs md:text-display-sm font-black tracking-normal leading-none">Grades that build live.</h3>
        <p className="mt-3 max-w-3xl text-sm text-ink-secondary italic">Grades measure ESPN market rank versus actual selection slot only. They do not pretend to know injuries, roster fit, or the future. Final cards become complete at {summary.totalPicks}/{summary.totalPicks} occupied slots.</p>
      </Reveal>
      <Reveal delay={80} className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {cards.map((card) => (
          <article key={card.manager} className="bg-bg-surface border-t-[3px] border-accent-gold p-5">
            <div className="flex items-center justify-between gap-3"><div className="flex items-center gap-2 min-w-0"><ManagerAvatar name={card.manager} size="sm" /><div className="min-w-0"><div className="font-black truncate">{card.manager}</div><div className="text-[9px] text-ink-tertiary truncate">{card.completedPicks}/{card.pickCount} picks</div></div></div><div className="font-display text-4xl font-black text-accent-gold">{card.marketGrade}</div></div>
            <div className="mt-4 h-1.5 bg-bg-deep"><div className="h-full bg-accent-gold" style={{ width: `${card.completion}%` }} /></div>
            {card.completedPicks ? <>
              <div className="mt-4 grid grid-cols-2 gap-2 text-[10px]">
                <div><span className="text-ink-tertiary">AVG VALUE</span><div className="mt-1 font-black">{card.averageValue >= 0 ? '+' : ''}{card.averageValue}</div></div>
                <div><span className="text-ink-tertiary">QB/RB/WR/TE</span><div className="mt-1 font-black">{card.positions.QB}/{card.positions.RB}/{card.positions.WR}/{card.positions.TE}</div></div>
              </div>
              {card.bestSteal && <div className="mt-4 text-[10px] text-ink-secondary"><span className="text-accent-gold font-black">BEST VALUE</span><br />{card.bestSteal.player} at {card.bestSteal.pickOverall}</div>}
              {card.strength && <div className="mt-3 text-[10px] text-ink-secondary"><span className="text-accent-gold font-black">MARKET STRENGTH</span><br />{card.strength}</div>}
              {card.starterCore.length > 0 && <div className="mt-3 text-[10px] text-ink-secondary"><span className="text-accent-gold font-black">STARTING CORE</span><br />{card.starterCore.slice(0, 4).map((player) => player.player).join(' · ')}</div>}
              {card.rosterWarnings.length > 0 && <div className="mt-3 text-[10px] text-accent-red"><span className="font-black">ROSTER CHECK</span><br />{card.rosterWarnings.slice(0, 3).join(' · ')}</div>}
            </> : <div className="mt-4 text-[11px] text-ink-muted italic">Awaiting draft selections.</div>}
          </article>
        ))}
      </Reveal>
      <div className="mt-5 flex items-start gap-3 bg-bg-base border-l-[3px] border-accent-gold p-4 max-w-3xl"><ShieldCheck size={18} className="text-accent-gold shrink-0" /><p className="text-[11px] text-ink-tertiary leading-relaxed">Report cards are deliberately labeled market grades. Positive value means a player was selected later than ESPN&apos;s dated rank; negative value means earlier. The site preserves the receipt without presenting it as a season prediction.</p></div>
    </div>
  );
}
